import { hasSDK } from '@littlebird/sdk';
import { type Token, type Tokens } from 'marked';
import {
  createElement,
  Fragment,
  memo,
  type CSSProperties,
  type ReactNode,
  useContext,
} from 'react';

import CopyButton from '@/components/blocks/CopyButton';
import { RoutineRecommendations } from '@/components/blocks/Message/RoutineRecommendations';
import { EntityReferenceChip } from '@/components/ui/EntityReferenceChip';
import { EntityReferenceLink } from '@/components/ui/EntityReferenceLink';
import { logChatRenderDebug } from '@/utils/chat-render-debug';
import { isSupportedEntityReference } from '@/utils/entity-reference';

import { Anchor } from './components/Anchor';
import { BlockLatex } from './components/StaticCode/Latex';
import { InlineCode, StaticCode } from './components/StaticCode/StaticCode';
import { isAllowedImageUrl } from './img-sanitizer';
import {
  EntityReferenceResolutionContext,
  SmoothdownRenderContext,
} from './smoothdown-render-context';
import {
  parseMarkdownIntoBlocks,
  simpleHash,
  type EntityReferenceToken,
} from './utils';

interface MemoizedMarkdownBlockProps {
  content: string;
  type: 'blockquote' | 'table' | 'space' | 'code' | (string & {});
  softBreaks?: boolean;
}

interface RenderMarkedOptions {
  editorTables?: boolean;
  softBreaks?: boolean;
}

const ROUTINES_LANGUAGE = 'routines';

function parseRoutineSlugs(content: string): string[] {
  return content.trim().split(/\s+/).filter(Boolean);
}

function logMarkdownBlockRender({
  content,
  softBreaks,
  type,
}: MemoizedMarkdownBlockProps) {
  const key = `${type}:${softBreaks ? 'soft' : 'hard'}:${simpleHash(content)}`;

  logChatRenderDebug(
    'markdown-block-render',
    {
      contentHash: simpleHash(content),
      contentLength: content.length,
      preview: content.slice(0, 120),
      softBreaks: Boolean(softBreaks),
      type,
    },
    key
  );
}

function HorizontalRule() {
  const { isAnimating } = useContext(SmoothdownRenderContext);

  return (
    <hr
      className={
        isAnimating ? 'motion-opacity-in-0 motion-delay-100' : undefined
      }
    />
  );
}

function MarkdownEntityReference({
  reference,
}: {
  reference: EntityReferenceToken;
}) {
  const canResolve = useContext(EntityReferenceResolutionContext);
  const { chatId } = useContext(SmoothdownRenderContext);
  if (!canResolve || !chatId || !isSupportedEntityReference(reference))
    return reference.raw;

  const Component = hasSDK() ? EntityReferenceLink : EntityReferenceChip;
  return <Component reference={reference} />;
}

function getInlineFallbackReason(tokens: Token[], path: string): string | null {
  for (const [index, token] of tokens.entries()) {
    const nextPath = `${path}.${token.type}[${index}]`;

    switch (token.type) {
      case 'text':
      case 'escape':
      case 'br':
      case 'codespan':
      case 'html':
      case 'image':
      case 'inlineMath':
      case 'entityReference':
        break;
      case 'strong':
      case 'em':
      case 'del': {
        if (!token.tokens) return `${nextPath}: missing inline tokens`;
        const reason = getInlineFallbackReason(token.tokens, nextPath);
        if (reason) return reason;
        break;
      }
      case 'link': {
        if (token.title) return `${nextPath}: link title`;
        if (!token.tokens) return `${nextPath}: missing link tokens`;
        const reason = getInlineFallbackReason(token.tokens, nextPath);
        if (reason) return reason;
        break;
      }
      default:
        return `${nextPath}: unsupported inline token`;
    }
  }

  return null;
}

function getListFallbackReason(list: Tokens.List, path: string): string | null {
  for (const [itemIndex, item] of list.items.entries()) {
    const itemPath = `${path}.item[${itemIndex}]`;

    for (const [tokenIndex, token] of item.tokens.entries()) {
      const tokenPath = `${itemPath}.${token.type}[${tokenIndex}]`;

      switch (token.type) {
        case 'text':
        case 'paragraph': {
          if (!token.tokens) return `${tokenPath}: missing inline tokens`;
          const reason = getInlineFallbackReason(token.tokens, tokenPath);
          if (reason) return reason;
          break;
        }
        case 'list': {
          const reason = getListFallbackReason(token as Tokens.List, tokenPath);
          if (reason) return reason;
          break;
        }
        case 'space':
        case 'hr':
        case 'math':
          break;
        case 'blockquote':
        case 'code':
        case 'table': {
          const reason = getBlockFallbackReason([token], itemPath);
          if (reason) return reason;
          break;
        }
        default:
          return `${tokenPath}: unsupported list item token`;
      }
    }
  }

  return null;
}

function getTableFallbackReason(
  table: Tokens.Table,
  path: string
): string | null {
  for (const [index, cell] of table.header.entries()) {
    const reason = getInlineFallbackReason(
      cell.tokens,
      `${path}.header[${index}]`
    );
    if (reason) return reason;
  }

  for (const [rowIndex, row] of table.rows.entries()) {
    for (const [cellIndex, cell] of row.entries()) {
      const reason = getInlineFallbackReason(
        cell.tokens,
        `${path}.row[${rowIndex}].cell[${cellIndex}]`
      );
      if (reason) return reason;
    }
  }

  return null;
}

function getBlockFallbackReason(tokens: Token[], path: string): string | null {
  for (const [index, token] of tokens.entries()) {
    const nextPath = `${path}.${token.type}[${index}]`;

    switch (token.type) {
      case 'space':
      case 'hr':
      case 'math':
        break;
      case 'paragraph': {
        const reason = getInlineFallbackReason(
          (token as Tokens.Paragraph).tokens,
          nextPath
        );
        if (reason) return reason;
        break;
      }
      case 'heading': {
        const reason = getInlineFallbackReason(
          (token as Tokens.Heading).tokens,
          nextPath
        );
        if (reason) return reason;
        break;
      }
      case 'list': {
        const reason = getListFallbackReason(token as Tokens.List, nextPath);
        if (reason) return reason;
        break;
      }
      case 'blockquote': {
        const reason = getBlockFallbackReason(
          (token as Tokens.Blockquote).tokens,
          nextPath
        );
        if (reason) return reason;
        break;
      }
      case 'code': {
        break;
      }
      case 'table': {
        const reason = getTableFallbackReason(token as Tokens.Table, nextPath);
        if (reason) return reason;
        break;
      }
      default:
        return `${nextPath}: unsupported block token`;
    }
  }

  return null;
}

function getMarkedFallbackReason(content: string, type: string): string {
  try {
    const tokens = parseMarkdownIntoBlocks(content.trim());
    if (tokens.length === 0) return 'empty token list';

    if (type === 'table') {
      if (tokens.length !== 1) return `table parsed as ${tokens.length} blocks`;
      if (tokens[0]?.type !== 'table') {
        return `table parsed as ${tokens[0]?.type ?? 'unknown'}`;
      }
    }

    return (
      getBlockFallbackReason(tokens, type) ?? 'unknown renderer null result'
    );
  } catch (error) {
    return `parse error: ${error instanceof Error ? error.message : String(error)}`;
  }
}

function MarkdownFallbackBlock({
  content,
  type,
}: {
  content: string;
  type: string;
}) {
  const reason = getMarkedFallbackReason(content, type);

  return (
    <div
      className={
        import.meta.env.DEV
          ? 'whitespace-pre-wrap break-words rounded-sm border border-dotted border-red-500/70 px-2 py-1'
          : 'whitespace-pre-wrap break-words'
      }
      data-smoothdown-fallback={type}
      title={
        import.meta.env.DEV ? `Unsupported markdown: ${reason}` : undefined
      }
    >
      {content}
    </div>
  );
}

function renderTextWithBreaks(
  text: string,
  keyPrefix: string,
  options?: RenderMarkedOptions
): ReactNode[] {
  const decodedText = decodeHtmlEntities(text);
  if (options?.softBreaks) return [decodedText];

  const lines = decodedText.split('\n');
  return lines.flatMap((line, index) =>
    index === 0 ? [line] : [<br key={`${keyPrefix}-br-${index}`} />, line]
  );
}

function decodeHtmlEntities(text: string) {
  if (!text.includes('&')) return text;

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

function renderMarkedImage(image: Tokens.Image, key: string): ReactNode {
  if (!isAllowedImageUrl(image.href)) return image.text || null;

  return (
    <img
      alt={image.text}
      className="float-image"
      key={key}
      loading="lazy"
      src={image.href}
      title={image.title || undefined}
    />
  );
}

function renderMarkedInlineTokens(
  tokens: Token[],
  keyPrefix: string,
  options?: RenderMarkedOptions
): ReactNode[] | null {
  const parts: ReactNode[] = [];

  for (const [index, token] of tokens.entries()) {
    const key = `${keyPrefix}-${index}`;

    switch (token.type) {
      case 'text':
      case 'escape':
        parts.push(...renderTextWithBreaks(token.text, key, options));
        break;
      case 'br':
        parts.push(<br key={key} />);
        break;
      case 'codespan':
        parts.push(<InlineCode key={key}>{token.text}</InlineCode>);
        break;
      case 'html':
        parts.push(
          ...renderTextWithBreaks(
            (token as Tokens.Generic).raw ?? token.text,
            key,
            options
          )
        );
        break;
      case 'image':
        parts.push(renderMarkedImage(token as Tokens.Image, key));
        break;
      case 'inlineMath':
        parts.push(
          <StaticCode key={key} className="language-math">
            {(token as Tokens.Generic).text}
          </StaticCode>
        );
        break;
      case 'entityReference': {
        const reference = token as EntityReferenceToken;
        parts.push(<MarkdownEntityReference key={key} reference={reference} />);
        break;
      }
      case 'strong': {
        if (!token.tokens) return null;
        const children = renderMarkedInlineTokens(token.tokens, key, options);
        if (!children) return null;
        parts.push(<strong key={key}>{children}</strong>);
        break;
      }
      case 'em': {
        if (!token.tokens) return null;
        const children = renderMarkedInlineTokens(token.tokens, key, options);
        if (!children) return null;
        parts.push(<em key={key}>{children}</em>);
        break;
      }
      case 'del': {
        if (!token.tokens) return null;
        const children = renderMarkedInlineTokens(token.tokens, key, options);
        if (!children) return null;
        parts.push(<del key={key}>{children}</del>);
        break;
      }
      case 'link': {
        if (token.title) return null;
        if (!token.tokens) return null;
        const children = renderMarkedInlineTokens(token.tokens, key, options);
        if (!children) return null;
        parts.push(
          <Anchor key={key} href={token.href}>
            {children}
          </Anchor>
        );
        break;
      }
      default:
        return null;
    }
  }

  return parts;
}

function renderMarkedList(
  list: Tokens.List,
  keyPrefix: string,
  options?: RenderMarkedOptions
): ReactNode | null {
  const itemContent = list.items.map((item, index) =>
    renderMarkedListItem(item, `${keyPrefix}-${index}`, options)
  );
  if (itemContent.some((item) => !item)) return null;

  const children = itemContent.map((item, index) => {
    const listItem = list.items[index];
    const isTaskItem = Boolean(listItem?.task);

    return (
      <li
        className={isTaskItem ? 'flex items-start' : undefined}
        data-checked={isTaskItem ? Boolean(listItem?.checked) : undefined}
        key={index}
      >
        {item}
      </li>
    );
  });
  const start = typeof list.start === 'number' ? list.start : undefined;
  const isTaskList = list.items.some((item) => item.task);

  return list.ordered ? (
    <ol start={start === 1 ? undefined : start}>{children}</ol>
  ) : (
    <ul
      className={isTaskList ? 'not-prose pl-2' : undefined}
      data-type={isTaskList ? 'taskList' : undefined}
    >
      {children}
    </ul>
  );
}

function renderMarkedListItem(
  item: Tokens.ListItem,
  keyPrefix: string,
  options?: RenderMarkedOptions
): ReactNode[] | null {
  const parts: ReactNode[] = [];

  for (const [index, token] of item.tokens.entries()) {
    const key = `${keyPrefix}-${index}`;

    switch (token.type) {
      case 'text':
      case 'paragraph': {
        const inlineTokens = token.tokens;
        if (!inlineTokens) return null;

        const children = renderMarkedInlineTokens(inlineTokens, key, options);
        if (!children) return null;
        parts.push(
          item.loose ? (
            <p key={key}>{children}</p>
          ) : (
            <Fragment key={key}>{children}</Fragment>
          )
        );
        break;
      }
      case 'list': {
        const list = renderMarkedList(token as Tokens.List, key, options);
        if (!list) return null;
        parts.push(<Fragment key={key}>{list}</Fragment>);
        break;
      }
      case 'space':
        break;
      case 'hr':
      case 'math':
      case 'blockquote':
      case 'code':
      case 'table': {
        const children = renderMarkedBlockTokens([token], key, options);
        if (!children) return null;
        parts.push(<Fragment key={key}>{children}</Fragment>);
        break;
      }
      default:
        return null;
    }
  }

  if (!item.task) return parts;

  return [
    <label contentEditable={false} key={`${keyPrefix}-checkbox-label`}>
      <input
        aria-label={`Task item checkbox for ${item.text}`}
        type="checkbox"
        checked={Boolean(item.checked)}
        disabled
        readOnly
      />
      <span />
    </label>,
    <div key={`${keyPrefix}-content`}>{parts}</div>,
  ];
}

function renderMarkedBlockTokens(
  tokens: Token[],
  keyPrefix: string,
  options?: RenderMarkedOptions
): ReactNode[] | null {
  const parts: ReactNode[] = [];

  for (const [index, token] of tokens.entries()) {
    const key = `${keyPrefix}-${index}`;

    switch (token.type) {
      case 'space':
        break;
      case 'paragraph': {
        const paragraph = token as Tokens.Paragraph;
        const children = renderMarkedInlineTokens(
          paragraph.tokens,
          key,
          options
        );
        if (!children) return null;
        parts.push(<p key={key}>{children}</p>);
        break;
      }
      case 'heading': {
        const heading = token as Tokens.Heading;
        const children = renderMarkedInlineTokens(heading.tokens, key, options);
        if (!children) return null;
        parts.push(createElement(`h${heading.depth}`, { key }, children));
        break;
      }
      case 'list': {
        const list = renderMarkedList(token as Tokens.List, key, options);
        if (!list) return null;
        parts.push(<Fragment key={key}>{list}</Fragment>);
        break;
      }
      case 'blockquote': {
        const blockquote = renderMarkedBlockquote(
          token as Tokens.Blockquote,
          key,
          options
        );
        if (!blockquote) return null;
        parts.push(<Fragment key={key}>{blockquote}</Fragment>);
        break;
      }
      case 'hr':
        parts.push(<HorizontalRule key={key} />);
        break;
      case 'math':
        parts.push(
          <BlockLatex key={key}>{(token as Tokens.Generic).text}</BlockLatex>
        );
        break;
      case 'code': {
        const code = token as Tokens.Code;
        const language = code.lang?.trim().split(/\s+/)[0] || 'text';
        if (language === ROUTINES_LANGUAGE) {
          parts.push(
            <RoutineRecommendations
              key={key}
              slugs={parseRoutineSlugs(code.text)}
            />
          );
          break;
        }
        parts.push(
          <StaticCode key={key} className={`language-${language}`}>
            {code.text}
          </StaticCode>
        );
        break;
      }
      case 'table': {
        const table = renderMarkedTable(token as Tokens.Table, options);
        if (!table) return null;
        parts.push(<Fragment key={key}>{table}</Fragment>);
        break;
      }
      default:
        return null;
    }
  }

  return parts;
}

function renderMarkedBlockquote(
  blockquote: Tokens.Blockquote,
  keyPrefix: string,
  options?: RenderMarkedOptions
): ReactNode | null {
  const children = renderMarkedBlockTokens(
    blockquote.tokens,
    keyPrefix,
    options
  );
  if (!children) return null;

  return (
    <blockquote className="[overflow-wrap:anywhere]">{children}</blockquote>
  );
}

function renderMarkedBlocks(
  content: string,
  keyPrefix = 'block',
  options?: RenderMarkedOptions
): ReactNode | null {
  try {
    const tokens = parseMarkdownIntoBlocks(content.trim(), {
      breaks: options?.softBreaks,
    });
    const children = renderMarkedBlockTokens(tokens, keyPrefix, options);
    if (!children || children.length === 0) return null;

    return <>{children}</>;
  } catch {
    return null;
  }
}

function isIndentedCopyableContent(content: string) {
  const firstContentLine = content
    .split(/\r?\n/)
    .find((line) => line.trim().length > 0);

  return Boolean(firstContentLine && /^(?: {2,}|\t)/.test(firstContentLine));
}

function renderMarkedCopyable(
  content: string,
  options?: RenderMarkedOptions
): ReactNode | null {
  if (isIndentedCopyableContent(content)) {
    return <StaticCode className="language-text">{content}</StaticCode>;
  }

  return renderMarkedBlocks(content, 'copyable', {
    ...options,
    editorTables: true,
  });
}

function getTableCellStyle(
  align: 'center' | 'left' | 'right' | null
): CSSProperties | undefined {
  return align ? { textAlign: align } : undefined;
}

function renderMarkedTable(
  table: Tokens.Table,
  options?: RenderMarkedOptions
): ReactNode | null {
  const headerCells = table.header.map((cell, index) => ({
    align: table.align[index] ?? null,
    content: renderMarkedInlineTokens(
      cell.tokens,
      `table-head-${index}`,
      options
    ),
  }));
  const bodyRows = table.rows.map((row, rowIndex) =>
    row.map((cell, cellIndex) => ({
      align: table.align[cellIndex] ?? null,
      content: renderMarkedInlineTokens(
        cell.tokens,
        `table-${rowIndex}-${cellIndex}`,
        options
      ),
    }))
  );

  if (
    headerCells.some((cell) => !cell.content) ||
    bodyRows.some((row) => row.some((cell) => !cell.content))
  ) {
    return null;
  }

  if (options?.editorTables) {
    return (
      <div className="tableWrapper">
        <table>
          <colgroup>
            {headerCells.map((_, index) => (
              <col key={index} />
            ))}
          </colgroup>
          <tbody>
            <tr className="lb-editor-table-row">
              {headerCells.map((cell, index) => (
                <th
                  className="lb-editor-table-cell"
                  key={index}
                  style={getTableCellStyle(cell.align)}
                >
                  <p>{cell.content}</p>
                </th>
              ))}
            </tr>
            {bodyRows.map((row, rowIndex) => (
              <tr className="lb-editor-table-row" key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td
                    className="lb-editor-table-cell"
                    key={cellIndex}
                    style={getTableCellStyle(cell.align)}
                  >
                    <p>{cell.content}</p>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <table>
      <thead>
        <tr>
          {headerCells.map((cell, index) => (
            <th key={index} style={getTableCellStyle(cell.align)}>
              {cell.content}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {bodyRows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <td key={cellIndex} style={getTableCellStyle(cell.align)}>
                {cell.content}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function renderMarkedTableFromContent(
  content: string,
  options?: RenderMarkedOptions
): ReactNode | null {
  try {
    const tokens = parseMarkdownIntoBlocks(content.trim());
    if (tokens.length !== 1 || tokens[0]?.type !== 'table') return null;

    return renderMarkedTable(tokens[0] as Tokens.Table, options);
  } catch {
    return null;
  }
}

function TableBlock({
  content,
  softBreaks,
}: {
  content: string;
  softBreaks?: boolean;
}) {
  const table = renderMarkedTableFromContent(content, { softBreaks });

  return (
    <div className="relative [&:hover>div]:opacity-100">
      <div className="absolute right-1 top-1 rounded-[var(--radius-sm)] bg-[var(--surface-active)] opacity-0 shadow-sm transition-opacity">
        <CopyButton
          size="xs"
          text={content}
          copyText="Copy Table"
          showTooltip={false}
        />
      </div>
      <div className="markdown-table-scroll">
        {table ?? <MarkdownFallbackBlock content={content} type="table" />}
      </div>
    </div>
  );
}

export const MemoizedMarkdownBlock = memo(
  ({ content, type, softBreaks }: MemoizedMarkdownBlockProps) => {
    logMarkdownBlockRender({ content, type, softBreaks });

    switch (type) {
      case 'table':
        return <TableBlock content={content} softBreaks={softBreaks} />;
      case 'space':
        return null;
      case 'hr':
        return (
          renderMarkedBlocks(content, 'hr', { softBreaks }) ?? (
            <HorizontalRule />
          )
        );
      case 'code':
      case 'heading':
      case 'list':
      case 'blockquote':
      case 'math':
      case 'paragraph': {
        const block = renderMarkedBlocks(content, type, { softBreaks });
        if (block) return block;
        return <MarkdownFallbackBlock content={content} type={type} />;
      }
      case 'copyable': {
        const copyable = renderMarkedCopyable(content, { softBreaks });
        if (copyable) return copyable;
        return <MarkdownFallbackBlock content={content} type="copyable" />;
      }
      default:
        return <MarkdownFallbackBlock content={content} type={type} />;
    }
  },
  (prevProps, nextProps) =>
    simpleHash(prevProps.content) === simpleHash(nextProps.content) &&
    prevProps.type === nextProps.type &&
    Boolean(prevProps.softBreaks) === Boolean(nextProps.softBreaks)
);
